import { IBalusterGeometry, IRailingConfig, IRailingGeometry, IStairConfig, IVec3 } from '@stair-craft/common/util';

import { balusterCount } from '../shared-baluster-count/baluster-count';
import { openingRectOf, placeUpperRailingsOnSlab } from '../shared-upper-railings-on-slab/upper-railings-on-slab';

/**
 * Back-edge (oz1) rail segments that skip the flight-2 exit zone, per turn direction.
 * Extracted from `generateUpperFloorRailing` to keep its complexity low.
 */
function buildBackEdges(
    isRight: boolean,
    ox1: number,
    ox2: number,
    oz1: number,
    exitX1: number,
    exitX2: number,
    absOz: number
): [number, number, number, number][] {
    const edges: [number, number, number, number][] = [];
    if (absOz <= 0) {
        return edges;
    }

    if (isRight) {
        // Flight 1 side: ox1 to exitX1
        if (ox1 < exitX1) {
            edges.push([ox1, oz1, Math.min(exitX1, ox2), oz1]);
        }
        // Flight 2 outer side: exitX2 to ox2
        if (exitX2 < ox2) {
            edges.push([exitX2, oz1, ox2, oz1]);
        }
    } else {
        // Flight 2 outer side: ox1 to exitX1
        if (ox1 < exitX1) {
            edges.push([ox1, oz1, exitX1, oz1]);
        }
        // Flight 1 side: exitX2 to ox2
        if (exitX2 < ox2) {
            edges.push([Math.max(exitX2, ox1), oz1, ox2, oz1]);
        }
    }

    return edges;
}

/**
 * Builds one upper-floor railing segment from an edge, or null when the edge is too short to rail.
 * Extracted from `generateUpperFloorRailing` to keep its complexity low.
 */
function buildEdgeRailing(edge: [number, number, number, number], y: number, rc: IRailingConfig, midX: number): IRailingGeometry | null {
    const [x1, z1, x2, z2]: [number, number, number, number] = edge;
    const edgeLength: number = Math.sqrt((x2 - x1) ** 2 + (z2 - z1) ** 2);
    if (edgeLength < 100) {
        return null;
    }

    const edgePosts: IBalusterGeometry[] = [
        { position: { x: x1, z: z1, y }, height: rc.height, rotation: 0 },
        { position: { x: x2, z: z2, y }, height: rc.height, rotation: 0 },
    ];

    const slots: number = balusterCount(edgeLength, rc);
    const edgeBalusters: IBalusterGeometry[] = [];
    for (let i: number = 1; i < slots; i++) {
        const frac: number = i / slots;
        edgeBalusters.push({
            position: { x: x1 + (x2 - x1) * frac, z: z1 + (z2 - z1) * frac, y },
            height: rc.height,
            rotation: 0,
        });
    }

    const handrail: { points: IVec3[]; width: number; height: number } = {
        points: [
            { x: x1, y: y + rc.height, z: z1 },
            { x: x2, y: y + rc.height, z: z2 },
        ],
        width: rc.handrailWidth,
        height: rc.handrailHeight,
    };

    const verticalSide: 'left' | 'right' = x1 <= midX ? 'left' : 'right';
    const edgeSide: 'left' | 'right' = x1 === x2 ? verticalSide : 'left';

    return {
        kind: 'upper',
        side: edgeSide,
        balusters: edgeBalusters,
        posts: edgePosts,
        balusterWidth: rc.balusterWidth,
        postWidth: rc.postWidth,
        handrail,
    };
}

export function generateUpperFloorRailing(config: IStairConfig): IRailingGeometry[] {
    const rc: IRailingConfig = config.railing;
    const y: number = config.floorHeight + config.floorThickness;
    const railings: IRailingGeometry[] = [];
    const isRight: boolean = config.turnDirection === 'right';
    const sw: number = config.stairWidth;

    // World coords (relative to room origin)
    const ox1: number = config.openingX;
    const ox2: number = config.openingX + config.openingWidth;
    const oz1: number = config.openingZ;
    const oz2: number = config.openingZ + config.openingLength;

    const absOz: number = config.openingZ;
    const rw: number = config.roomWidth;
    const rl: number = config.roomLength;

    // Exit zone: flight 2 exits at the back edge (oz1) on flight 2 side — in world coords now
    const f2LeftLocal: number = isRight ? sw + config.flightGap : -(sw + config.flightGap);
    const f2RightLocal: number = isRight ? 2 * sw + config.flightGap : -config.flightGap;
    // Translate to world: stair group nominally at openingX (right turn) or openingX+openingWidth-sw (left turn)
    const stairAnchorX: number = isRight ? config.openingX : config.openingX + config.openingWidth - sw;
    const exitX1: number = stairAnchorX + Math.min(f2LeftLocal, f2RightLocal);
    const exitX2: number = stairAnchorX + Math.max(f2LeftLocal, f2RightLocal);

    // Back edge (oz1): split — exclude flight 2 exit zone, keep both sides
    const edges: [number, number, number, number][] = buildBackEdges(isRight, ox1, ox2, oz1, exitX1, exitX2, absOz);

    // Front edge (oz2): full railing if not against front wall
    if (absOz + config.openingLength < rl) {
        edges.push([ox1, oz2, ox2, oz2]);
    }

    if (ox1 > 0) {
        edges.push([ox1, oz1, ox1, oz2]);
    }
    if (ox2 < rw) {
        edges.push([ox2, oz1, ox2, oz2]);
    }

    const midX: number = (ox1 + ox2) / 2;
    for (const edge of edges) {
        const railing: IRailingGeometry | null = buildEdgeRailing(edge, y, rc, midX);
        if (railing) {
            railings.push(railing);
        }
    }

    placeUpperRailingsOnSlab(railings, openingRectOf(config), config.railing.balusterSpacing, {
        width: config.roomWidth,
        length: config.roomLength,
    });
    return railings;
}
